// MassiveMessageModal.tsx
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../app/hooks";
import { ObjectTarget } from "../../app/permissions";
import { RootState } from "../../app/store";
import { INVALID_ID, LoadState } from "../../redux/common";
import { surveyInsert, surveyInsUpdSlice } from "../../redux/survey/surveyInsUpdSlice";
import { SurveyInfo, SurveyQuestionInfo, SurveyQuestionOptionInfo, SurveyQuestionType, SurveyStatus, SurveyType } from "../../redux/survey/surveySlice";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import SurveyPreview from "./SurveyPreview";

interface SurveyModalProps {
    objectName: string;
    open: boolean;
    schoolId: string;
    target: ObjectTarget;
    targetId?: string | number;
    onClose: (success: boolean) => void;
}

const SurveyModal = (props: SurveyModalProps) => {
    const { objectName, open } = props;

    const dispatch = useAppDispatch();

    const [onPreview, setOnPreview] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [showError, setShowError] = useState(false);
    const [surveyType, setSurveyType] = useState<SurveyType>(SurveyType.PRIVATE_PER_USER);
    const [options, setOptions] = useState<SurveyQuestionOptionInfo[]>([]);
    
    const getDefaultEndDateTime = (): Dayjs => {
        return dayjs().add(7, 'day').set('hour', 22).set('minute', 0).set('second', 0);
    };

    const [selectedDate, setSelectedDate] = useState(getDefaultEndDateTime());

    const surveyInsUpd = useSelector((state: RootState) => state.surveyInsUpd);

    function onClose(success: boolean) {
        setOnPreview(false);
        setTitle("");
        setDescription("");
        setShowError(false);
        setSurveyType(SurveyType.PRIVATE_PER_USER);
        setOptions([]);
        setSelectedDate(getDefaultEndDateTime());
        dispatch(surveyInsUpdSlice.actions.reset());
        props.onClose(success);
    }

    function buildSurvey(): SurveyInfo {
        return {
            id: INVALID_ID,
            title: title,
            description: description,
            schoolId: props.schoolId,
            type: surveyType,
            target: props.target,
            targetId: props.targetId,
            status: SurveyStatus.NOT_STARTED,
            endAt: selectedDate.toISOString(),
            questions: [
                {
                    id: "1",
                    title: title,
                    description: description,
                    type: SurveyQuestionType.OPTIONS,
                    options: options,
                } as SurveyQuestionInfo
            ]
        } as SurveyInfo;
    }

    function onSend() {
        const survey = buildSurvey();
        dispatch(surveyInsert({survey}));
    }

    useEffect(() => {
        if (surveyInsUpd.state.state === LoadState.Loaded) {
            onClose(true);
        }
        if (surveyInsUpd.state.state === LoadState.Failed) {
            setShowError(true);
        }
    }, [surveyInsUpd]);

    const sending = surveyInsUpd.state.state === LoadState.Loading;

    return (
        <>
            <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
                {onPreview &&
                    <>
                        <DialogTitle>Preview da Enquete</DialogTitle>
                        <SurveyPreview 
                            survey={buildSurvey()}
                        />
                        <DialogActions>
                            <Button onClick={() => setOnPreview(false)}
                                disabled={sending} variant="outlined">
                                Voltar
                            </Button>
                            <Button onClick={onSend} 
                                disabled={!isAllOk}
                                variant="contained" >
                                Enviar
                            </Button>
                        </DialogActions>
                    </>
                }
                {!onPreview && elEditing()}
            </Dialog>
            <Snackbar open={showError} autoHideDuration={5000} onClose={() => setShowError(false)}>
                <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: "100%" }}>
                    Erro ao enviar mensagem.
                </Alert>
            </Snackbar>
        </>
    );
    
    function getSurveyTypeTooltip(type: SurveyType): React.ReactNode {
        switch (type) {
            case SurveyType.GROUP:
                return <Paper elevation={10} sx={{ padding: 1 }}>
                    <Typography>
                        A enquete será enviada para o 
                        <Box component={"span"} sx={{color: "red"}}><b> grupo ({objectName}) </b></Box>
                        e permitirá que 
                        <Box component={"span"} sx={{color: "blue"}}><b> todos os usuários </b></Box>
                        participantes do grupo votem.
                    </Typography>
                </Paper>;
            case SurveyType.PRIVATE_PER_STUDENT:
                return <Paper elevation={10} sx={{ padding: 1 }}>
                    <Typography>
                        A enquete será enviada para cada aluno individualmente em sua 
                        <Box component={"span"} sx={{color: "red"}}><b> conversa privada ({objectName}) </b></Box>.
                    </Typography>
                    <Typography>
                        Cada aluno poderá responder apenas uma vez, ou seja, os familiares poderão gerar apenas um voto para este aluno.
                    </Typography>
                </Paper>;
            case SurveyType.PRIVATE_PER_USER:
                return <Paper elevation={10} sx={{ padding: 1 }}>
                    <Typography>
                        A enquete será enviada para cada usuário individualmente em sua 
                        <Box component={"span"} sx={{color: "red"}}><b> conversa privada ({objectName}) </b></Box>.
                    </Typography>
                    <Typography>
                        Cada usuário poderá responder uma vez, ou seja, cada familiar poderá gerar um voto para a enquete.
                    </Typography>
                </Paper>;
            default:
                return "Selecione um tipo de enquete para ver mais informações.";
        }
    }

    function elEditing() {
        return <>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6" component="div">
                        Enquete
                    </Typography>
                    <DateTimePicker
                        value={selectedDate}
                        onChange={(newValue) => {
                            if (newValue) setSelectedDate(newValue);
                        }}
                        disabled={sending}
                        minDateTime={minDateTime()}
                        maxDateTime={maxDateTime()}
                        label="Data e hora de término"
                        format="DD/MMM/YYYY HH:mm:ss"
                        ampm={false}
                        slotProps={{ textField: { size: "small" } }}
                    />
                </Box>
            </DialogTitle>
                <DialogContent>
                    {elSurveyType()}
                    {elSurveyTitle()}
                    {elSurveyDescription()}

                    <Typography variant="h6" sx={{ marginTop: "0px" }}>
                        Alernativas
                    </Typography>

                    {options.map((option, index) => (
                        <Box key={`option-${index}`} sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                            <TextField
                                key={index}
                                autoFocus
                                margin="dense"
                                id={`option-${option.id}`}
                                label={`Alternativa ${option.id}`}
                                type="text"
                                fullWidth
                                value={option.title}
                                error={!isOptionOk(option)}
                                disabled={sending}
                                onChange={e => {
                                    const newOptions = [...options];
                                    newOptions[index].title = e.target.value;
                                    setOptions(newOptions);
                                }} />
                            <IconButton
                                color="error"
                                disabled={sending}
                                onClick={() => {
                                    const newOptions = options.filter((_, i) => i !== index);
                                    for (let i = 0; i < newOptions.length; i++) {
                                        newOptions[i].id = `${i + 1}`;
                                    }
                                    setOptions(newOptions);
                                }}>
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}

                    <Button variant="contained" onClick={() => {
                        const newQuestion: SurveyQuestionOptionInfo = {
                            id: `${options.length + 1}`,
                            title: "",
                            description: "",
                        };
                        setOptions([...options, newQuestion]);
                    }} sx={{ marginTop: "10px", marginBottom: "10px" }}>
                        <AddCircleIcon />
                    </Button>

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose(false)}
                        disabled={sending} variant="outlined">
                        Cancelar
                    </Button>
                    <Button onClick={() => setOnPreview(true)}
                        disabled={!isAllOk()}
                        color="warning"
                        variant="contained" >
                        Preview
                    </Button>
                    {/* <Button onClick={onSend} 
                        disabled={!isAllOk()}
                        variant="contained" >
                        Enviar
                    </Button> */}
                </DialogActions>
        </>;
    }

    function minDateTime(): dayjs.Dayjs | undefined {
        return dayjs().add(1, 'day');
    }

    function maxDateTime(): dayjs.Dayjs | undefined {
        return dayjs().add(30, 'day');
    }

    function elSurveyType() {
        return (
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                <FormControl fullWidth margin="dense">
                    <InputLabel id="survey-type-label">Tipo da Enquete</InputLabel>
                    <Select
                        labelId="survey-type-label"
                        id="survey-type"
                        value={surveyType}
                        label="Tipo da Enquete"
                        disabled={sending}
                        onChange={(e) => setSurveyType(e.target.value as SurveyType)}
                    >
                        {/* <MenuItem value={SurveyType.GROUP}>
                            Grupo - Votação no grupo - Uma resposta por usuário
                        </MenuItem> */}
                        <MenuItem value={SurveyType.PRIVATE_PER_STUDENT}>
                            Por Aluno - Conversa privada - Uma resposta por aluno
                        </MenuItem>
                        <MenuItem value={SurveyType.PRIVATE_PER_USER}>
                            Por Usuário - Conversa privada - Uma resposta por usuário
                        </MenuItem>
                    </Select>
                </FormControl>
                <Tooltip title={getSurveyTypeTooltip(surveyType)} placement="right">
                    <Box>
                        <InfoIcon />
                    </Box>
                </Tooltip>
            </Box>
        );
    }

    function isOptionOk(option: SurveyQuestionOptionInfo) {
        return option.title.trim().length > 0;
    }

    function isAllOk() {
        return isTitleOk() && isDescriptionOk() && isEndDateTimeOk() && isMinimumOk() && options.every(isOptionOk);
    }

    function isMinimumOk() {
        return options.length > 1;
    }

    function isTitleOk() {
        return title.trim().length > 5;
    }

    function elSurveyTitle() {
        return (
            <TextField
                autoFocus
                margin="dense"
                id="title"
                label="Título da enquete"
                type="text"
                fullWidth
                value={title}
                disabled={sending}
                error={!isTitleOk()}
                onChange={e => setTitle(e.target.value)} />
        );
    }

    function isDescriptionOk() {
        return description.trim().length > 10;
    }

    function isEndDateTimeOk() {
        return selectedDate.isAfter(minDateTime()) && selectedDate.isBefore(maxDateTime());
    }

    function elSurveyDescription() {
        return (
            <TextField
                autoFocus
                margin="dense"
                id="description"
                label="Descrição da enquete"
                type="text"
                fullWidth
                multiline
                rows={10}
                value={description}
                disabled={sending}
                error={!isDescriptionOk()}
                onChange={e => setDescription(e.target.value)} />
        );
    }
};

export default SurveyModal;